import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/path-planning-for-autonomous-ships/path-planning-for-autonomous-ships.webp';
import RadarSystemsImage from 'assets/resources/path-planning-for-autonomous-ships/RADAR-systems-enable.webp';
import DynamicObstacleDetectionImage from 'assets/resources/path-planning-for-autonomous-ships/dynamic-obstacle-detection.webp';
import OptimizingCollisionAvoidanceRoutingImage from 'assets/resources/path-planning-for-autonomous-ships/optimizing-collision-avoidance-routing.webp';
import ArtificialPotentialFieldMethodsImage from 'assets/resources/path-planning-for-autonomous-ships/artificial-potential-field-methods.webp';
import PathPlanningThroughMarineTrafficManagementImage from 'assets/resources/path-planning-for-autonomous-ships/path-planning-through-marine-traffic-management.webp';
import StrengtheningRealTimeRiskAssessmentImage from 'assets/resources/path-planning-for-autonomous-ships/strengthening-risk-assessment.webp';
import COLREGSFrameworkImage from 'assets/resources/path-planning-for-autonomous-ships/COLREGS-framework.webp';

const commonData = {
  ...AUTHORS['Michael Haralson'],
  title:
    'Path Planning For Autonomous Ships With Sensor Fusion: How To Navigate Dense Traffic Under Uncertainty',
  publishDate: 'October 23, 2025',
  minutesOfRead: 15,
};

export const forthOctober2025ArticleMeta: PageMeta = {
  url: '/articles/path-planning-for-autonomous-ships',
  title: commonData.title,
  description:
    'Autonomous ships navigate blind through fog-choked ports where GPS dies, merging RADAR and LiDAR to dodge collisions milliseconds before disaster strikes.',
};

export const forthOctober2025ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <p>
        Autonomous ships combine RADAR, LiDAR, and AIS data through <strong>sensor fusion</strong>{' '}
        to create real-time obstacle maps in crowded ports where GPS fails. W-band RADAR cuts
        through fog at 76-77GHz with centimeter accuracy, while LiDAR fires millions of laser points
        per second to track moving vessels beyond 200 meters. Machine learning algorithms merge
        COLREGs rules with reinforcement learning to calculate <strong>collision-free paths</strong>
        , though split-second decisions in <strong>dense traffic</strong> remain tricky. Velocity
        obstacle methods and <strong>dynamic window approaches</strong> filter achievable maneuvers
        when uncertainty complicates predictions, and the details get technical fast.
      </p>
    ),
    ...commonData,
  },
  {
    type: 'list',
    title: 'Key Points',
    listItems: [
      'Sensor fusion combines RADAR, LiDAR, and AIS data to achieve centimeter-level accuracy and reduce false alarms in dense traffic scenarios.',
      'Deep reinforcement learning and LSTM-RL hybrids enable adaptive path planning that improves navigation performance in high-density environments.',
      'Modified velocity obstacle methods predict collisions using real-time data while incorporating maneuverability limits and COLREGs compliance.',
      'Multi-objective cost functions balance collision risk, fuel consumption, and regulatory compliance with adaptive weighting for different maritime environments.',
      'Dynamic Window Approach filters achievable trajectories using live sensor data and assigns collision hazard scores for split-second decision-making.',
    ],
    text: (
      <p>
        AILiveSim&apos;s expertise areas are in high-fidelity maritime & defense simulation,
        multi-sensor synthetic data generation (radar/LiDAR/EO-IR), digital-twin scenario authoring,
        and scalable training/validation pipelines for autonomous navigation—AILiveSim aims to build
        trust in AI synthetic data and our company, and we are trusted by our customers. Visit our
        website:{' '}
        <a href="https://www.ailivesim.com" target="_blank" rel="noopener noreferrer">
          AILiveSim
        </a>
      </p>
    ),
  },
  {
    type: 'common',
    title: 'RADAR Systems Enable Real-Time Sensor Adaptation for Path Planning Under Uncertainty',
    text: (
      <>
        <p>
          When <strong>autonomous ships</strong> hit crowded waterways or lose GPS signal,{' '}
          <strong>RADAR</strong> essentially becomes their lifeline for{' '}
          <strong>real-time sensor adaptation</strong>. Those W-band systems operating at 76–77GHz?
          They’re delivering <strong>centimeter-level accuracy</strong>—precise enough to thread
          through tight harbor spaces.
        </p>

        <p>
          And here’s the thing: <strong>fog and rain</strong> that would blind cameras and LiDAR
          don’t faze RADAR at all.
        </p>

        <p>
          Now, fuse this with AIS and video feeds. You get <strong>combined tracks</strong> that
          cross-verify what’s actually out there, which appears to slash false alarms considerably.{' '}
          <strong>AI algorithms</strong> process this integrated data stream, adjusting routes on
          the fly when obstacles pop up.
        </p>

        <blockquote>
          Sensor fusion cross-verifies targets through RADAR, AIS, and cameras—dramatically cutting
          false positives while AI dynamically reroutes around emerging threats.
        </blockquote>

        <p>
          That said, the real magic might be during <strong>GPS spoofing</strong> or jamming
          incidents—terrain-based radar matching seems to keep position locked even then. Satellite
          gone dark? The system keeps running.
        </p>

        <p>
          What’s particularly interesting is how software upgrades are transforming standard marine
          radars into autonomous navigation workhorses. No massive hardware overhaul needed. With
          maritime trade volumes{' '}
          <a
            href="https://navtechradar.com/news/using-radar-to-confront-challenges/"
            target="_blank"
            rel="noopener noreferrer"
          >
            expected to triple
          </a>{' '}
          by 2050, scalable sensor solutions that don’t require fleet-wide hardware replacement
          become increasingly critical.
        </p>

        <p>
          The{' '}
          <a
            href="https://www.maritimerobotics.com/autonomous-navigation-system"
            target="_blank"
            rel="noopener noreferrer"
          >
            360-degree situational awareness
          </a>{' '}
          that integrated radar systems provide ensures vessels maintain comprehensive environmental
          monitoring even as conditions shift.{' '}
          <a
            href="https://promwad.com/news/navigation-systems-autonomous-ships"
            target="_blank"
            rel="noopener noreferrer"
          >
            Machine learning algorithms
          </a>{' '}
          enable these systems to analyze sensor data in real-time, improving decision-making as
          vessels encounter dynamic environmental changes. Still, we should probably acknowledge
          that centimeter precision in all conditions remains aspirational for most setups. Even so,
          the trajectory is clear: RADAR’s becoming the anchor technology when other sensors fail or
          conditions deteriorate.
        </p>
      </>
    ),
    image: RadarSystemsImage,
  },
  {
    type: 'common',
    title:
      'Dynamic Obstacle Detection Through LiDAR Sensors for Autonomous Ship Path Planning in Dense Traffic',
    text: (
      <>
        <p>
          LiDAR sensors are throwing millions of laser points per second at the water, building{' '}
          <strong>3D maps</strong> with <strong>centimeter-level accuracy</strong>. That kind of
          precision? It matters when you’re threading through harbor traffic or dodging debris.
        </p>

        <p>
          These systems can detect objects over 200 meters out, day or night—something cameras
          simply can’t match. The real magic happens when <strong>real-time processing</strong>{' '}
          kicks in, tracking velocity and trajectories of moving vessels. All that data feeds into
          adaptive <strong>path planning algorithms</strong> like RRT and VFH, which appear to
          reroute dynamically as traffic patterns shift around them.
        </p>

        <p>
          <strong>Key LiDAR capabilities for dense traffic navigation:</strong>
          <ul>
            <li>
              360-degree panoramic coverage from mast-mounted sensors gives ships eyes everywhere
            </li>
            <li>
              Near-instantaneous obstacle detection crunches through millions of points per second
            </li>
            <li>
              Object classification seems to distinguish vessel types from floating hazards, though
              accuracy likely varies with conditions
            </li>
            <li>
              Sensor fusion integration with radar appears to improve recognition accuracy—at least
              in most scenarios
            </li>
            <li>
              Dynamic corridor generation maintains what are essentially collision-free paths
              through evolving conditions, though “collision-free” might be optimistic in truly
              chaotic situations
            </li>
          </ul>
        </p>

        <p>
          The technology’s evolution toward{' '}
          <a
            href="https://www.neuvition.com/media/blog/smart-ships-integrating-lidar-transforming-maritime-operations.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            smaller, lighter sensors
          </a>{' '}
          is expanding deployment options across different vessel classes and configurations.
          Processing workflows typically involve{' '}
          <a
            href="https://www.nature.com/articles/s41598-024-60327-9"
            target="_blank"
            rel="noopener noreferrer"
          >
            data acquisition, denoising, and filtering
          </a>{' '}
          before coordinate transformation converts raw points into actionable navigational
          intelligence. While LiDAR performs consistently across varying lighting conditions,{' '}
          <a
            href="https://thinkrobotics.com/blogs/learn/lidar-based-obstacle-avoidance-the-ultimate-guide-to-autonomous-navigation-technology"
            target="_blank"
            rel="noopener noreferrer"
          >
            weather detection algorithms
          </a>{' '}
          adjust operational parameters during heavy rain or fog to maintain reliable obstacle
          detection.
        </p>
      </>
    ),
    image: DynamicObstacleDetectionImage,
  },
  {
    type: 'common',
    title:
      'Optimizing Collision Avoidance Routing With Collision Avoidance Algorithms in Dense Traffic Under Uncertainty',
    text: (
      <>
        <p>
          Dense traffic doesn’t wait for perfect information, and neither do{' '}
          <strong>collision avoidance algorithms</strong>. That said,{' '}
          <strong>approximate representation reinforcement learning</strong> seems to offer a
          promising middle ground—it merges COLREGs with prior knowledge and handles continuous
          state-space environments fairly efficiently.
        </p>

        <p>
          When it comes to <strong>multi-ship chaos, deep RL</strong> appears to tackle the problem
          through <strong>scene segmentation</strong>, though it’s worth noting that performance
          still drops by 12.6% in high-density settings. Even so, that’s considerably better than
          baseline models, which lose anywhere from 18.4% to 45.2% of their effectiveness.
        </p>

        <p>
          The <strong>Beam Search Algorithm</strong> takes a different approach. It crunches{' '}
          <strong>trajectories</strong> for 30 vessels simultaneously—no small feat—while ensuring
          minimal <strong>closest point of approach</strong> distances. On top of that,{' '}
          <strong>modified velocity obstacle methods</strong> can predict collisions by analyzing
          velocities and courses, essentially fusing AIS and ECDIS data to maintain real-time
          awareness.
        </p>

        <p>
          It’s likely that this fusion approach works best when data quality is consistent, though
          real-world conditions may challenge that assumption. The integration of{' '}
          <a
            href="https://onlinelibrary.wiley.com/doi/10.1155/2022/1534815"
            target="_blank"
            rel="noopener noreferrer"
          >
            ship maneuverability constraints
          </a>{' '}
          alongside COLREGs compliance ensures that collision-free navigation remains feasible even
          as traffic complexity increases.
        </p>

        <p>
          LSTM-RL hybrids have shown they can outperform model-free policies, particularly in
          unknown waters where adaptability matters most. The learning process typically stabilizes
          around the{' '}
          <a
            href="https://www.frontiersin.org/journals/marine-science/articles/10.3389/fmars.2022.1084763/full"
            target="_blank"
            rel="noopener noreferrer"
          >
            1500th training epoch
          </a>
          , marking the point where optimal path performance becomes consistent.
        </p>

        <p>
          Meanwhile, <strong>evolutionary algorithms</strong> generate what might be considered
          ideal paths, at least in theory, while respecting international rules. Whether these
          “ideal” paths hold up under the messy realities of actual maritime traffic remains an open
          question, but the approach certainly shows promise.
        </p>
      </>
    ),
    image: OptimizingCollisionAvoidanceRoutingImage,
  },

  {
    type: 'common',
    title:
      'Artificial Potential Field Methods and Local Maneuver Optimization for Path Planning in Congested Waters',
    text: (
      <>
        <p>
          Adaptive repulsive fields can adjust their force based on how fast obstacles are moving
          and how close they get—though the exact tuning often depends on the specific scenario.
        </p>

        <p>
          When vessels find themselves stuck in obstacle-dense regions,{' '}
          <strong>virtual sub-goals</strong> seem to offer a way out of the deadlock. That said,
          implementing these can be tricky when multiple ships converge simultaneously.
        </p>

        <p>
          Some <strong>APF variants</strong> now incorporate <strong>COLREGS compliance</strong>,
          essentially trying to align the mathematical maneuvers with what maritime law actually
          requires. It’s an interesting approach, even if the translation from regulations to force
          fields isn’t always straightforward.
        </p>

        <p>
          On top of that, researchers have started using <strong>ellipsoidal fields</strong> to
          better capture the actual shape of ships—after all, most vessels aren’t perfect circles.
          This appears to enable tighter passages while (hopefully) maintaining safety margins.
        </p>

        <p>
          Perhaps most telling is how multi-objective cost functions attempt to juggle everything at
          once: <strong>collision risk</strong>, <strong>fuel consumption</strong>, and staying on
          the right side of regulations.
        </p>

        <p>
          The weighting between these factors likely varies depending on whether you’re navigating a{' '}
          <strong>busy strait</strong> or <strong>open waters</strong>, though finding the right
          balance remains something of an art. Improved algorithms have shown that smoother
          trajectories naturally reduce fluctuations in{' '}
          <a
            href="https://www.nature.com/articles/s41598-024-78025-x"
            target="_blank"
            rel="noopener noreferrer"
          >
            heading angle and yaw rate
          </a>
          , which contributes to overall vessel stability. Hybrid approaches now generate{' '}
          <a
            href="https://repository.tudelft.nl/record/uuid:8a59f5fd-bf25-4c01-9019-0cf747672255"
            target="_blank"
            rel="noopener noreferrer"
          >
            global and local paths
          </a>{' '}
          separately to address different scales of the navigation problem more effectively. The
          integration of{' '}
          <a
            href="https://ui.adsabs.harvard.edu/abs/2019JNav...72..588L/abstract"
            target="_blank"
            rel="noopener noreferrer"
          >
            new virtual forces
          </a>{' '}
          helps ensure that autonomous vessels maintain appropriate collision avoidance maneuvers
          even when encountering unpredictable strategies from other ships.
        </p>
      </>
    ),
    image: ArtificialPotentialFieldMethodsImage,
  },

  {
    type: 'common',
    title:
      'Path Planning Through Marine Traffic Management: The Role of Velocity Obstacle Method Under Uncertainty',
    text: (
      <>
        <p>
          Predicting collisions in crowded waters? It’s a moving target—literally. The{' '}
          <strong>Velocity Obstacle method</strong> essentially maps out which velocity sets would
          lead ships straight into collision, creating a <strong>real-time visualization</strong> of
          danger zones.
        </p>

        <p>
          Now, modified VO algorithms take things further, adapting for the actual{' '}
          <strong>maneuverability limits</strong> ships face and, crucially,{' '}
          <strong>COLREGs compliance</strong>.
        </p>

        <p>
          But here’s where it gets tricky: <strong>uncertainty</strong> from{' '}
          <strong>sensor fusion errors</strong> and incomplete vessel data seems to complicate just
          about everything. When your radar gives you one reading and AIS another, or when that
          fishing vessel ahead isn’t broadcasting at all, the neat mathematical models start to
          wobble.
        </p>

        <p>
          That said, <strong>asymmetric grey cloud models</strong> appear to offer a way forward,
          quantifying this ambiguity while computing collision-free velocity sets on the fly.
        </p>

        <p>
          What’s particularly interesting is how the system handles multiple traffic participants at
          once. Rather than treating each vessel in isolation, it generates what might be called{' '}
          <strong>minimal evasive actions</strong>—just enough to avoid collision while maintaining
          route efficiency. Hybrid approaches combining VO with{' '}
          <a
            href="https://ui.adsabs.harvard.edu/abs/2024IJNAO..1600586Y/abstract"
            target="_blank"
            rel="noopener noreferrer"
          >
            A path optimization
          </a>{' '}
          have shown they can reduce collision avoidance distances by notable margins in
          multi-encounter scenarios.
        </p>

        <p>
          The <strong>real-time feedback control</strong> then executes these velocity commands,
          though one wonders how well this translates when seas get rough or visibility drops.
        </p>

        <p>
          The promise is compelling: <strong>autonomous vessels</strong> navigating congested sea
          lanes safely without breaking international navigation rules. Even so, the gap between
          computed ideal velocities and what a vessel can actually achieve in practice likely
          remains a challenge worth watching.
        </p>
      </>
    ),
    image: PathPlanningThroughMarineTrafficManagementImage,
  },
  {
    type: 'common',
    title:
      'Strengthening Risk Assessment for Autonomous Ship Path Planning With Dynamic Window Approach',
    text: (
      <>
        <p>
          Maneuvering <strong>crowded harbors</strong> at speed demands split-second decisions, and
          that’s where <strong>Dynamic Window Approach</strong> earns its keep.
        </p>

        <p>
          Here’s the thing—DWA filters candidate trajectories to only what’s physically achievable.
          No fantasy moves allowed. It assigns each path a{' '}
          <strong>collision hazard probability score</strong>, which seems to separate death traps
          from safe routes pretty effectively.
        </p>

        <blockquote>
          DWA keeps ships honest—only real maneuvers make the cut, then probabilistic scoring
          filters catastrophic paths from survivable ones.
        </blockquote>

        <p>
          Meanwhile, <strong>sensor fusion</strong> pumps in live radar, camera, and AIS data. The{' '}
          <strong>decision loop</strong>? Constantly updating. When obstacles shift positions, DWA
          appears to recalculate avoidance points on the fly, though the exact speed of this
          recalculation likely depends on computational resources.
        </p>

        <p>
          <strong>Key DWA capabilities for real-time risk mitigation:</strong>
          <ul>
            <li>
              Constrains trajectories within vessel kinematic limits—essentially ensuring the ship
              doesn’t attempt a turn it can’t physically make.
            </li>
            <li>
              Assigns probabilistic risk values to each candidate path, though these probabilities
              may vary in accuracy depending on sensor quality.
            </li>
            <li>
              Updates collision avoidance points as sensor data changes (assuming the sensors
              themselves remain reliable).
            </li>
            <li>
              Appears to handle multiple moving obstacles simultaneously, even in dense traffic like
              container ships weaving between fishing vessels.
            </li>
            <li>
              Selects what should be the lowest-risk trajectory, attempting to balance safety and
              efficiency—though “lowest risk” might mean different things to different operators.
            </li>
          </ul>
        </p>
      </>
    ),
    image: StrengtheningRealTimeRiskAssessmentImage,
  },
  {
    type: 'common',
    title: 'COLREGS Framework: Governing Sensor Fusion for Path Planning in Dense Maritime Traffic',
    text: (
      <>
        <p>
          Split-second avoidance maneuvers don’t mean much if they break international maritime law.
        </p>

        <p>
          Here’s the thing: <strong>COLREGs</strong>—the <strong>International Regulations</strong>{' '}
          for Preventing Collisions at Sea—weren’t exactly written with robots in mind. The rules
          talk about “masters” and “crew,” assuming there’s a human at the helm.
        </p>

        <p>
          Now <strong>sensor fusion systems</strong> are trying to encode these regulations into{' '}
          <strong>algorithm</strong>, crunching data from radar, AIS, GPS, and cameras to rank
          collision risks in milliseconds.
        </p>

        <p>
          But there’s a problem. Many COLREGs seem to require the kind of{' '}
          <strong>subjective judgment</strong> that’s hard to code—what looks “safe” to one captain
          might appear risky to another.
        </p>

        <p>
          The IMO’s scoping exercise has already flagged serious regulatory gaps when it comes to{' '}
          <strong>unmanned vessels</strong>. Path planners have to decide whether to “give-way” or
          “stand-on” based on vessel type and those tricky Rule 9 channel restrictions.
        </p>

        <p>
          Meanwhile, the technology keeps surging forward. Legal frameworks? They’re still playing
          catch-up, and it’s not clear they’ll close the gap anytime soon.
        </p>

        <p>
          Interested in synthetic data for your project? AILiveSim 2.0 (our new version!) enhances
          AI-based simulation for multi-sensor autonomous systems - automating data generation,
          analysis, and augmentation to streamline model training and testing. Find out more:{' '}
          <a href="https://ailivesim.com" target="_blank" rel="noopener noreferrer">
            AILiveSim
          </a>
        </p>
      </>
    ),
    image: COLREGSFrameworkImage,
  },

  {
    type: 'common',
    title: 'Did you know ?',
    text: ``,
    submenu: [
      {
        type: 'common',
        title: 'How Does Sensor Fusion Handle GPS-Denied Navigation in Coastal Areas?',
        text: (
          <p>
            When GPS fails near coasts, sensor fusion combines IMU dead reckoning, visual odometry
            (tracking features like outcroppings), LiDAR, and radar. Kalman filters weigh inputs,
            while map-based relocalization corrects drift—assuming coastal maps stay current despite
            storms and development.
          </p>
        ),
      },
      {
        type: 'common',
        title:
          'What Computational Requirements Are Needed for Real-Time Multi-Sensor Fusion Processing?',
        text: (
          <p>
            When radar, LiDAR, and camera streams hit multi-core processors simultaneously,
            we&apos;re talking 10-500 GFLOPS and 1-64 GB RAM. The real challenge? Sub-100ms latency
            in dense traffic. GPUs and FPGAs aren&apos;t nice-to-have anymore—they&apos;re essential
            for hitting these targets consistently.
          </p>
        ),
      },
      {
        type: 'common',
        title: 'How Do Different Sensor Fusion Algorithms Compare in Latency Performance?',
        text: (
          <p>
            EKF delivers moderate latency for real-time maritime use. Particle filters bog down from
            sampling complexity. AI-driven fusion demands substantial processing but can outpace
            traditional methods with GPU acceleration—though it struggles on CPU-bound systems.
          </p>
        ),
      },
      {
        type: 'common',
        title:
          'What Calibration Methods Ensure Spatial Alignment Across Heterogeneous Sensor Types?',
        text: (
          <p>
            Getting wildly different sensors to agree on where things are? That&apos;s where
            target-based calibration comes in—using fiducial markers like checkerboards or ArUco
            tags. Then there&apos;s the hand-eye transformation puzzle, linking each sensor&apos;s
            frame to the vehicle&apos;s reference system.
          </p>
        ),
      },
      {
        type: 'common',
        title: 'How Does Sensor Fusion Degradation Affect Path Planning Safety Margins?',
        text: (
          <p>
            When sensor fusion degrades, path planners expand obstacle avoidance zones—a defensive
            admission that the system can&apos;t trust its own eyes. Bigger buffers around objects,
            slower speeds. Less certainty means wider berths, though finding the sweet spot between
            caution and efficiency isn&apos;t trivial.
          </p>
        ),
      },
    ],
  },
  {
    type: 'common',
    title: 'Parting Shot',
    text: (
      <>
        <p>
          Listen. <strong>RADAR</strong>, <strong>LiDAR</strong>, <strong>AIS</strong>,{' '}
          <strong>visual sensors</strong>—they merge through adaptive algorithms at maritime
          autonomy’s cutting edge. <strong>Dense traffic</strong> doesn’t wait. Can you imagine
          navigating through a swarm of vessels when your sensors feed conflicting data, when
          uncertainty clouds every calculation, when milliseconds separate success from catastrophe?
        </p>

        <p>Ships move. Sensors fail. Uncertainty never sleeps.</p>

        <p>
          Think about this: <strong>COLREGS compliance</strong> demands <strong>precision</strong>;
          velocity obstacles require prediction; artificial potential fields need constant
          recalculation. Together, they form a puzzle. Together, they must solve themselves.
          Together, they determine whether an <strong>autonomous vessel</strong> threads safely
          through congested waters or becomes tomorrow’s headline. The technology works—mostly. When
          sensors align, when algorithms converge, when probabilistic models accurately predict the
          chaos of maritime traffic, these systems demonstrate remarkable capability.
        </p>

        <p>Mostly.</p>

        <p>
          But here’s what matters: failure isn’t theoretical. Out there, where salt spray blinds
          cameras and radar ghosts dance across screens, that’s where probabilistic models face
          their trial. Real water. Real stakes. Real consequences. You know the difference between
          simulation and reality? One forgives errors; the other doesn’t.
        </p>

        <p>
          Consider the chain of decisions: <strong>sensor fusion</strong> processes terabytes of
          data, <strong>path-planning algorithms</strong> evaluate thousands of trajectories,{' '}
          <strong>collision-avoidance systems</strong> trigger in fractions of seconds—all while
          waves pound the hull, while weather degrades visibility, while human-operated vessels
          behave unpredictably. The margin for error shrinks with every added variable.{' '}
          <strong>Complexity compounds</strong>. Risk multiplies.
        </p>

        <p>
          Yet the promise remains irresistible. Autonomous ships could revolutionize maritime
          transport, reduce human error, optimize fuel consumption. If, and only if, we master this
          dance between certainty and chaos.
        </p>

        <p>The catch? Technology must perform flawlessly when flawlessness seems impossible.</p>
      </>
    ),
  },
];

export const forthOctober2025ArticleCard: TCardProps = {
  description:
    'This article shows how sensor fusion builds real-time maps when GPS fails. It contrasts VO, DWA, APF, and RL planners balancing COLREGs, risk, and fuel. It flags data gaps, latency, and the need for synthetic edge-case training.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: forthOctober2025ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.AutonomousVehicles,
    EHashtag.MachineLearning,
    EHashtag.Robotics,
    EHashtag.ObjectDetection,
    EHashtag.SafetyValidation,
    EHashtag.MaritimeTechnology,
  ],
};
